import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { 
  TrendingUp, 
  Users, 
  Eye, 
  MousePointerClick, 
  MapPin, 
  Calendar,
  RefreshCw,
  ExternalLink,
  BarChart3,
  Activity
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AnalyticsManager = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState({
    totalVisits: 0,
    totalBookings: 0,
    conversionRate: 0,
    topPages: [],
    locations: [],
    recentActivity: []
  });
  const [timeRange, setTimeRange] = useState('7d'); // 7d, 30d, 90d

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/analytics?range=${timeRange}`);
      const data = await response.json();
      
      if (data.success) {
        setAnalytics(data.analytics);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast({
        title: 'Error',
        description: 'Failed to load analytics data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ icon: Icon, title, value, subtitle, trend }) => (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">{title}</p>
            <p className="text-3xl font-bold mt-2">{value}</p>
            {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
          </div>
          <div className={`p-3 rounded-full ${trend === 'up' ? 'bg-green-100' : 'bg-blue-100'}`}>
            <Icon className={`w-6 h-6 ${trend === 'up' ? 'text-green-600' : 'text-blue-600'}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-3 text-gray-600">Loading analytics...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Website Analytics</h2>
          <p className="text-sm text-gray-600 mt-1">Track website traffic, conversions, and customer behavior</p>
        </div>
        <div className="flex gap-2">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
          </select>
          <Button onClick={fetchAnalytics} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid md:grid-cols-4 gap-4">
        <StatCard
          icon={Eye}
          title="Total Visits"
          value={analytics.totalVisits.toLocaleString()}
          subtitle="Unique visitors"
          trend="up"
        />
        <StatCard
          icon={MousePointerClick}
          title="Total Bookings"
          value={analytics.totalBookings.toLocaleString()}
          subtitle="Completed bookings"
          trend="up"
        />
        <StatCard
          icon={TrendingUp}
          title="Conversion Rate"
          value={`${analytics.conversionRate.toFixed(2)}%`}
          subtitle="Visitors to bookings"
          trend="up"
        />
        <StatCard
          icon={Activity}
          title="Avg. Session"
          value={analytics.avgSessionDuration || 'N/A'}
          subtitle="Time on site"
        />
      </div>

      {/* Top Pages & Locations */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Top Pages */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <BarChart3 className="w-5 h-5 mr-2 text-blue-600" />
              Top Pages
            </CardTitle>
          </CardHeader>
          <CardContent>
            {analytics.topPages && analytics.topPages.length > 0 ? (
              <div className="space-y-3">
                {analytics.topPages.map((page, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{page.path || 'Home'}</p>
                      <p className="text-xs text-gray-600">{page.title || 'Page'}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-blue-600">{page.views.toLocaleString()}</p>
                      <p className="text-xs text-gray-500">views</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <BarChart3 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No page data available</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Locations */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <MapPin className="w-5 h-5 mr-2 text-green-600" />
              Top Locations
            </CardTitle>
          </CardHeader>
          <CardContent>
            {analytics.locations && analytics.locations.length > 0 ? (
              <div className="space-y-3">
                {analytics.locations.map((location, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <MapPin className="w-4 h-4 text-green-600" />
                      <div>
                        <p className="font-medium text-gray-900">{location.city}</p>
                        <p className="text-xs text-gray-600">{location.state}, {location.country}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-green-600">{location.visits.toLocaleString()}</p>
                      <p className="text-xs text-gray-500">visits</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <MapPin className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No location data available</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Conversion Funnel */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <TrendingUp className="w-5 h-5 mr-2 text-purple-600" />
            Conversion Funnel
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="relative">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Landing Page Visits</span>
                <span className="text-sm font-bold text-gray-900">{analytics.funnelSteps?.landing || 0}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div className="bg-blue-600 h-3 rounded-full" style={{ width: '100%' }}></div>
              </div>
            </div>
            <div className="relative">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Services Page Views</span>
                <span className="text-sm font-bold text-gray-900">{analytics.funnelSteps?.services || 0}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div className="bg-blue-600 h-3 rounded-full" style={{ width: `${(analytics.funnelSteps?.services / analytics.funnelSteps?.landing * 100) || 0}%` }}></div>
              </div>
            </div>
            <div className="relative">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Booking Page Opens</span>
                <span className="text-sm font-bold text-gray-900">{analytics.funnelSteps?.booking || 0}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div className="bg-green-600 h-3 rounded-full" style={{ width: `${(analytics.funnelSteps?.booking / analytics.funnelSteps?.landing * 100) || 0}%` }}></div>
              </div>
            </div>
            <div className="relative">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Completed Bookings</span>
                <span className="text-sm font-bold text-gray-900">{analytics.totalBookings || 0}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div className="bg-green-600 h-3 rounded-full" style={{ width: `${analytics.conversionRate}%` }}></div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Google Analytics Link */}
      <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-bold text-gray-900 mb-1">Google Analytics Dashboard</h3>
              <p className="text-sm text-gray-600">View detailed analytics, real-time data, and advanced reports</p>
            </div>
            <Button 
              onClick={() => window.open('https://analytics.google.com', '_blank')}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Open GA4
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Setup Instructions */}
      {analytics.totalVisits === 0 && (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardHeader>
            <CardTitle className="text-yellow-800">Setup Required</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 text-sm text-yellow-900">
              <p className="font-medium">To start tracking analytics:</p>
              <ol className="list-decimal list-inside space-y-2 ml-2">
                <li>Create a Google Analytics 4 property at <a href="https://analytics.google.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">analytics.google.com</a></li>
                <li>Copy your Measurement ID (format: G-XXXXXXXXXX)</li>
                <li>Replace 'G-XXXXXXXXXX' in the HTML head with your Measurement ID</li>
                <li>Deploy your website</li>
                <li>Wait 24-48 hours for data to start appearing</li>
              </ol>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AnalyticsManager;
